import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { setPageSEO } from "../utils/seo";
import { 
  Brain, Zap, Trophy, BookOpen, Gamepad2, TrendingUp, 
  ChevronRight, Play, Star, Users, Shield, ArrowRight, Youtube
} from "lucide-react";
import { Button } from "../components/ui/button";

// Assets
const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";
const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/voul2owj_3D353EDF-CD0A-45BF-91C8-A3C7C08106BD.jpeg";
const BRYCE_LAURA_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/zce3wy51_me%20and%20laura.jpg";

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [btcPrice, setBtcPrice] = useState(null);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    document.title = "Bitcoin Brain — Bitcoin Education for Working Families";
    setPageSEO({
      title: "Bitcoin Brain — Bitcoin Education for Working Families",
      description: "The Bitcoin course for beginners — built for working families who deserve the truth about money. 21 sections. Plain English. No subscription.",
      path: "/"
    });
    // Fetch BTC price
    api.get("/widgets/bitcoin-price").then(res => {
      setBtcPrice(res.data);
    }).catch(() => {});

    // Hide splash after 3 seconds
    const timer = setTimeout(() => setShowSplash(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Splash Screen
  if (showSplash) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${FOUNDER_IMAGE})` }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative z-10 text-center px-6 max-w-2xl"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-zinc-400 text-lg md:text-xl leading-relaxed"
          >
            This started with a $50 Christmas gift card.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-zinc-500 text-base md:text-lg mt-4"
          >
            December 2020. California.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="text-[#F7931A] text-2xl md:text-3xl font-heading font-bold mt-8"
          >
            It became Bitcoin Brain.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
            className="text-white text-xl md:text-2xl mt-8"
          >
            Welcome.
          </motion.p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-darker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-10 h-10 rounded-lg" />
              <span className="font-heading text-xl font-bold text-white">
                BITCOIN <span className="text-[#F7931A]">BRAIN</span>
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/mission" className="nav-link">Mission</Link>
              <Link to="/pricing" className="nav-link">Pricing</Link>
              {isAuthenticated ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="btn-primary"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login" className="nav-link">Login</Link>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="btn-primary"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </div>
              )}
            </div>
            {/* Mobile menu */}
            <div className="md:hidden flex items-center gap-3">
              {!isAuthenticated && (
                <Link to="/login" className="text-zinc-400 text-sm hover:text-white transition-colors">
                  Login
                </Link>
              )}
              <Button 
                onClick={() => navigate(isAuthenticated ? "/dashboard" : "/register")}
                className="btn-primary text-sm px-4"
                data-testid="mobile-cta-btn"
              >
                {isAuthenticated ? "Dashboard" : "Start"}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 sm:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F7931A]/5 to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="badge-orange inline-flex items-center gap-2 mb-6">
                <Zap size={14} />
                <span>Earn BTC XP as you learn</span>
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight">
                LEARN <span className="text-[#F7931A]">BITCOIN</span>.
                <br />
                OWN <span className="text-[#F7931A]">IT</span>.
              </h1>
              <p className="text-zinc-400 text-lg mt-6 max-w-lg">
                The Bitcoin course for beginners — built for working families who deserve the truth about money. 21 sections. Plain English. No subscription.
              </p>
              {btcPrice && (
                <div className="mt-6 inline-flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2">
                  <span className="text-zinc-500 text-sm">BTC/USD</span>
                  <span className="font-mono text-xl text-white">
                    ${btcPrice.price?.toLocaleString()}
                  </span>
                  <span className={`text-sm ${btcPrice.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {btcPrice.change_24h >= 0 ? '+' : ''}{btcPrice.change_24h?.toFixed(2)}%
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-4 mt-8">
                <Button 
                  onClick={() => navigate("/register")}
                  className="btn-primary text-lg px-8 py-4"
                  data-testid="hero-cta-btn"
                >
                  Start Learning Free <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  onClick={() => navigate("/demo")}
                  className="btn-secondary text-lg px-8 py-4"
                  data-testid="hero-demo-btn"
                >
                  Try Free Demo
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <img 
                  src={MASCOT_IMAGE} 
                  alt="Bitcoin Brain Mascot" 
                  className="w-full max-w-md mx-auto rounded-2xl btc-glow-lg"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#0A0A0A] border border-[#F7931A]/50 rounded-lg px-4 py-2 btc-glow-sm">
                  <p className="text-[#F7931A] font-mono text-sm">21M Supply Cap</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white">
              EVERYTHING YOU NEED TO <span className="text-[#F7931A]">UNDERSTAND</span> BITCOIN
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
              A complete Bitcoin education platform designed for regular people, not tech experts
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: "21-Section Course", desc: "5 modules covering everything from basics to advanced concepts" },
              { icon: Gamepad2, title: "Learn by Playing", desc: "Quiz Arena and Bitcoin Simulator make learning addictive" },
              { icon: Zap, title: "Earn BTC XP Rewards", desc: "Level up by completing lessons and quizzes" },
              { icon: Brain, title: "AI-Powered", desc: "Personal AI assistant to answer your Bitcoin questions" },
              { icon: TrendingUp, title: "Live Widgets", desc: "Real-time price, fear/greed index, and mempool data" },
              { icon: Trophy, title: "Track Progress", desc: "Achievements, badges, and leaderboard competition" },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="card-dark p-6 hover:border-[#F7931A]/50 transition-all"
              >
                <feature.icon className="text-[#F7931A] mb-4" size={32} />
                <h3 className="font-heading text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Preview */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white">
                THE COMPLETE <span className="text-[#F7931A]">BITCOIN</span> COURSE
              </h2>
              <p className="text-zinc-400 mt-4">
                21 sections across 5 modules. Each section includes video lessons, 
                reading mode, interactive elements, quizzes, and BTC XP rewards.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { num: 1, title: "Why Bitcoin", sections: "2 Sections" },
                  { num: 2, title: "What Bitcoin Is", sections: "3 Sections" },
                  { num: 3, title: "How It Works", sections: "5 Sections" },
                  { num: 4, title: "How To Use It", sections: "6 Sections" },
                  { num: 5, title: "Mindset", sections: "5 Sections" },
                ].map((module) => (
                  <div key={module.num} className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-white/5">
                    <div className="w-10 h-10 bg-[#F7931A]/20 rounded-lg flex items-center justify-center">
                      <span className="text-[#F7931A] font-bold">{module.num}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{module.title}</h4>
                      <p className="text-zinc-500 text-sm">{module.sections}</p>
                    </div>
                    <ChevronRight className="text-zinc-600" size={20} />
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={FOUNDER_IMAGE} 
                alt="Course Preview" 
                className="w-full rounded-2xl btc-glow"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent rounded-2xl" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[#F7931A] font-heading text-xl font-bold">
                  "IN BITCOIN WE TRUST"
                </p>
                <p className="text-zinc-400 text-sm mt-2">
                  Learn from someone who has lived it, not just studied it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white">
            PAY ONCE. <span className="text-[#F7931A]">OWN FOREVER.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
            No subscriptions. No recurring charges. One payment unlocks everything. 
            Used by homeschool families as a complete sound money curriculum.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Button 
              onClick={() => navigate("/pricing")}
              className="btn-primary text-lg px-8"
              data-testid="pricing-cta-btn"
            >
              Get Bitcoin Brain <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              onClick={() => navigate("/register")}
              className="btn-secondary text-lg px-8"
              data-testid="pricing-plans-btn"
            >
              Try Free Demo
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-8 mt-6 text-sm text-zinc-500">
            <span>One-time payment — no recurring charges</span>
            <span>Used by homeschool families as Bitcoin curriculum</span>
            <span>Built by a general construction apprentice and father of three</span>
          </div>
        </div>
      </section>

      {/* Bryce & Laura Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative">
              <img
                src={BRYCE_LAURA_IMAGE}
                alt="Bryce and Laura Fields"
                className="w-full rounded-2xl border-2 border-[#F7931A]/20"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl font-bold text-white mb-4">
                BUILT FOR <span className="text-[#F7931A]">FAMILIES</span>
              </h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                "I built Bitcoin Brain because I am a general construction apprentice, a husband to Laura, a father of three, 
                and I was tired of working harder and falling further behind."
              </p>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                "My first satoshis were bought with a $50 Christmas gift card in 2020. I hurt my back. Got laid up. 
                Built this on a laptop with five years of Bitcoin knowledge and an AI."
              </p>
              <p className="text-[#F7931A] text-sm font-medium">
                Learn Bitcoin. Own It.
              </p>
              <Button 
                onClick={() => navigate("/mission")}
                className="btn-secondary mt-4"
              >
                Read the Full Story <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-8 h-8 rounded-lg" />
              <span className="font-heading text-lg font-bold text-white">
                BITCOIN <span className="text-[#F7931A]">BRAIN</span>
              </span>
            </div>
            <div className="flex items-center gap-8 text-sm text-zinc-500">
              <Link to="/mission" className="hover:text-[#F7931A] transition-colors">Mission</Link>
              <Link to="/pricing" className="hover:text-[#F7931A] transition-colors">Pricing</Link>
              <Link to="/legal" className="hover:text-[#F7931A] transition-colors">Legal</Link>
              <a href="https://www.youtube.com/@BitcoinBrain21m" target="_blank" rel="noopener noreferrer" className="hover:text-[#F7931A] transition-colors" data-testid="footer-youtube-link">
                <Youtube size={20} />
              </a>
              <span>Built by Bryce Fields | 2026</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
